import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { Toaster } from './components/ui/toaster';
import { roleCanAccessPath, getDefaultHomeForRole, isNotarialStaffRole } from './lib/roleLabels';
import { useEntitlements } from './hooks/useFeature';
import { TenantProvider } from './contexts/TenantContext';
import * as P from './routes/lazyPages';
import InstituteRouteGuard from './components/institute/InstituteRouteGuard';
import InstituteLayout from './components/layout/InstituteLayout';
import './App.css';

const InstituteAppRoute = ({ children }) => (
  <InstituteRouteGuard>
    <InstituteLayout>{children}</InstituteLayout>
  </InstituteRouteGuard>
);

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 dark:border-blue-400" />
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={getDefaultHomeForRole(user?.role)} replace />;
  }

  return children;
};

const CitizenRoute = ({ children, featureFlag = null }) => {
  const { features, loading: featureLoading } = useEntitlements();
  if (featureFlag && featureLoading) return <LoadingScreen />;
  if (featureFlag && features?.[featureFlag] !== true) {
    return <Navigate to="/citizen/dashboard" replace />;
  }
  return <ProtectedRoute allowedRoles={['citizen']}>{children}</ProtectedRoute>;
};

const STAFF_ROLES = [
  'operator', 'reviewer', 'titular', 'signer', 'archivist', 'admin', 'superadmin', 'platform_superadmin',
  'ventanilla_unica', 'field_supervisor', 'field_operator',
  'notary_public', 'notary_substitute', 'legal_drafter', 'notarial_assistant',
  'pld_compliance', 'tax_accounting', 'billing', 'notarial_archive', 'reception',
];

const NotarialClientRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/portal-notario/login" replace />;
  if (user?.role !== 'notarial_client') {
    return <Navigate to={getDefaultHomeForRole(user?.role)} replace />;
  }
  return children;
};

/** /backoffice → home según rol (notario → escritorio notarial) */
const BackofficeHomeRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getDefaultHomeForRole(user?.role)} replace />;
};

/** Staff con control de acceso por path según rol y feature flag */
const StaffRoute = ({ children, adminOnly = false, ventanillaOnly = false, featureFlag = null }) => {
  const { isAuthenticated, user, isSuperadmin, loading } = useAuth();
  const { features, loading: featureLoading } = useEntitlements();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/staff-login" replace />;
  if (!STAFF_ROLES.includes(user?.role) && !isSuperadmin) {
    return <Navigate to={getDefaultHomeForRole(user?.role)} replace />;
  }

  if (featureFlag && featureLoading) return <LoadingScreen />;
  if (featureFlag && features?.[featureFlag] !== true && !isSuperadmin) {
    const home = getDefaultHomeForRole(user?.role);
    // Evitar bucle si el home del rol exige el mismo feature
    if (
      home === location.pathname
      || (home.startsWith('/backoffice/notaria') && location.pathname.startsWith('/backoffice/notaria'))
    ) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
          <p className="text-sm text-slate-600 dark:text-slate-300 text-center max-w-md">
            El módulo notarial no está habilitado en la membresía de esta institución.
            Contacte al administrador de plataforma.
          </p>
        </div>
      );
    }
    return <Navigate to={home} replace />;
  }

  // Vertical notarial: personal notarial + admin/titular de membresía con módulo notarial
  const isNotarialRoute =
    location.pathname.startsWith('/backoffice/notaria')
    || (featureFlag && String(featureFlag).startsWith('notarial'));
  if (isNotarialRoute && !isNotarialStaffRole(user?.role)) {
    const notarialEnabled = features?.notarial_operations === true || isSuperadmin;
    const canAdminNotarial = (user?.role === 'admin' || user?.role === 'titular') && notarialEnabled;
    if (!canAdminNotarial && !isSuperadmin) {
      return <Navigate to={getDefaultHomeForRole(user?.role)} replace />;
    }
  }

  const isAdminOrSuper = user?.role === 'admin' || isSuperadmin;

  if (adminOnly && !isAdminOrSuper) {
    return <Navigate to={getDefaultHomeForRole(user.role)} replace />;
  }

  if (ventanillaOnly) {
    if (user.role !== 'ventanilla_unica' && !isAdminOrSuper) {
      return <Navigate to={getDefaultHomeForRole(user.role)} replace />;
    }
    return children;
  }

  // Ventanilla solo puede acceder a su módulo (y rutas explícitas ventanillaOnly)
  if (user.role === 'ventanilla_unica') {
    return <Navigate to="/backoffice/ventanilla" replace />;
  }

  if (!isAdminOrSuper && !roleCanAccessPath(user.role, location.pathname)) {
    // Permitir bandeja detalle si tiene acceso a escritorio
    if (
      location.pathname.startsWith('/backoffice/bandeja/') &&
      roleCanAccessPath(user.role, '/backoffice/escritorio')
    ) {
      return children;
    }
    return <Navigate to={getDefaultHomeForRole(user.role)} replace />;
  }

  return children;
};

function ClienteInstitutionRoute() {
  return (
    <TenantProvider>
      <P.ClienteLandingPage />
    </TenantProvider>
  );
}

function NotarioInstitutionRoute() {
  return (
    <TenantProvider>
      <P.NotarioInstitutionPage />
    </TenantProvider>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<P.LandingPage />} />
        <Route path="/cliente/:slug" element={<ClienteInstitutionRoute />} />
        <Route path="/notarios" element={<P.NotariosLandingPage />} />
        <Route path="/notarios/:slug" element={<NotarioInstitutionRoute />} />
        <Route path="/soy-notario" element={<P.SoyNotarioPage />} />
        <Route path="/instituto" element={<P.InstitutoLandingPage />} />
        <Route path="/instituto/login" element={<P.InstitutoLoginPage />} />
        <Route path="/instituto/escritorio" element={<InstituteAppRoute><P.InstitutoEscritorioPage /></InstituteAppRoute>} />
        <Route path="/instituto/bandeja" element={<InstituteAppRoute><P.InstitutoBandejaPage /></InstituteAppRoute>} />
        <Route path="/instituto/padron" element={<InstituteAppRoute><P.InstitutoPadronPage /></InstituteAppRoute>} />
        <Route path="/instituto/patentes" element={<InstituteAppRoute><P.InstitutoPatentesPage /></InstituteAppRoute>} />
        <Route path="/instituto/patentes/:id" element={<InstituteAppRoute><P.InstitutoPatenteDetailPage /></InstituteAppRoute>} />
        <Route path="/instituto/sellos-firmas" element={<InstituteAppRoute><P.InstitutoSellosFirmasPage /></InstituteAppRoute>} />
        <Route path="/instituto/libros-protocolos" element={<InstituteAppRoute><P.InstitutoLibrosProtocolosPage /></InstituteAppRoute>} />
        <Route path="/instituto/inspecciones" element={<InstituteAppRoute><P.InstitutoInspeccionesPage /></InstituteAppRoute>} />
        <Route path="/instituto/quejas-procedimientos" element={<InstituteAppRoute><P.InstitutoQuejasProcedimientosPage /></InstituteAppRoute>} />
        <Route path="/instituto/archivo" element={<InstituteAppRoute><P.InstitutoArchivoPage /></InstituteAppRoute>} />
        <Route path="/instituto/testamentos" element={<InstituteAppRoute><P.InstitutoTestamentosPage /></InstituteAppRoute>} />
        <Route path="/instituto/expedientes-compartidos" element={<InstituteAppRoute><P.InstitutoExpedientesCompartidosPage /></InstituteAppRoute>} />
        <Route path="/instituto/estadistica" element={<InstituteAppRoute><P.InstitutoEstadisticaPage /></InstituteAppRoute>} />
        <Route path="/instituto/configuracion" element={<InstituteAppRoute><P.InstitutoConfiguracionPage /></InstituteAppRoute>} />
        <Route path="/login" element={<P.AuthPage />} />
        <Route path="/register" element={<P.AuthPage initialTab="register" />} />
        <Route path="/staff-login" element={<P.StaffLoginPage />} />
        <Route path="/portal-notario/login" element={<P.NotarialClientLoginPage />} />
        <Route path="/verify" element={<P.VerifySignaturePage />} />
        <Route path="/verify/:code" element={<P.VerifySignaturePage />} />

        {/* Citizen Routes */}
        <Route path="/citizen/dashboard" element={<CitizenRoute><P.CitizenDashboard /></CitizenRoute>} />
        <Route path="/citizen/vault" element={<CitizenRoute><P.VaultPage /></CitizenRoute>} />
        <Route path="/citizen/procedures" element={<CitizenRoute><P.ProceduresPage /></CitizenRoute>} />
        <Route path="/citizen/procedures/:id" element={<CitizenRoute><P.ProcedureDetailPage /></CitizenRoute>} />
        <Route path="/citizen/requests" element={<CitizenRoute><P.RequestsListPage /></CitizenRoute>} />
        <Route path="/citizen/requests/:id" element={<CitizenRoute><P.RequestDetailPage /></CitizenRoute>} />
        <Route path="/citizen/pagos" element={<CitizenRoute featureFlag="citizen_payments"><P.PaymentPage /></CitizenRoute>} />
        <Route path="/citizen/payments" element={<CitizenRoute featureFlag="citizen_payments"><P.PaymentPage /></CitizenRoute>} />
        <Route path="/citizen/reports" element={<CitizenRoute featureFlag="citizen_reports"><P.CitizenReportsPage /></CitizenRoute>} />
        <Route path="/citizen/reports/new" element={<CitizenRoute featureFlag="citizen_reports"><P.CitizenReportsPage /></CitizenRoute>} />
        <Route path="/citizen/profile" element={<CitizenRoute><P.CitizenProfilePage /></CitizenRoute>} />
        <Route path="/citizen/notifications" element={<CitizenRoute><P.CitizenNotificationsPage /></CitizenRoute>} />
        <Route path="/citizen/appointments" element={<CitizenRoute featureFlag="citizen_appointments"><P.CitizenAppointmentsPage /></CitizenRoute>} />
        <Route path="/citizen/queue" element={<CitizenRoute featureFlag="virtual_queue"><P.CitizenQueuePage /></CitizenRoute>} />
        <Route path="/pago-exitoso" element={<CitizenRoute featureFlag="citizen_payments"><P.PaymentPage /></CitizenRoute>} />
        <Route path="/pago-cancelado" element={<CitizenRoute featureFlag="citizen_payments"><P.PaymentPage /></CitizenRoute>} />

        <Route path="/portal-notario" element={<NotarialClientRoute><P.NotarialClientDashboard /></NotarialClientRoute>} />
        <Route path="/portal-notario/expedientes/:id" element={<NotarialClientRoute><P.NotarialClientMatterPage /></NotarialClientRoute>} />

        {/* Backoffice — home */}
        <Route
          path="/backoffice"
          element={
            <StaffRoute>
              <BackofficeHomeRedirect />
            </StaffRoute>
          }
        />
        <Route path="/backoffice/despacho" element={<StaffRoute featureFlag="despacho_digital"><P.DespachoDigitalPage /></StaffRoute>} />
        <Route path="/backoffice/instrucciones" element={<StaffRoute featureFlag="instrucciones"><P.InstruccionesPage /></StaffRoute>} />
        <Route path="/backoffice/instrucciones/:id" element={<StaffRoute featureFlag="instrucciones"><P.InstruccionDetailPage /></StaffRoute>} />
        <Route path="/backoffice/semaforo-cumplimiento" element={<StaffRoute><P.SemaforoCumplimientoPage /></StaffRoute>} />
        <Route path="/backoffice/acuerdos-gabinete" element={<StaffRoute featureFlag="acuerdos_gabinete"><P.AcuerdosGabinetePage /></StaffRoute>} />
        <Route path="/backoffice/cabildo" element={<StaffRoute featureFlag="cabildo_digital"><P.CabildoPage /></StaffRoute>} />
        <Route path="/backoffice/notaria" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialDashboardPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/expedientes" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialMattersPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/expedientes/:id" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialMatterDetailPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/testimonios" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialTestimoniesPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/instrumentos" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialInstrumentsPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/plantillas" element={<StaffRoute featureFlag="notarial_templates"><P.NotarialTemplatesPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/obligaciones" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialObligationsPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/registros" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialRegistriesPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/agenda" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialAppointmentsPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/cotizaciones" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialQuotesPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/cumplimiento" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialCompliancePage /></StaffRoute>} />
        <Route path="/backoffice/notaria/portal" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialPortalPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/reportes" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialReportsPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/seguridad" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialSecurityPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/integraciones" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialIntegrationsPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/clientes" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialClientsPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/protocolo" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialProtocolPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/actos" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialActosPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/pld" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialPLDPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/config" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialConfigPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/entidad" element={<StaffRoute featureFlag="notarial_operations"><P.NotarialEntidadPage /></StaffRoute>} />
        <Route path="/backoffice/notaria/configuracion/chiapas" element={<StaffRoute featureFlag="notarial_chiapas_jurisdiction"><P.NotarialChiapasConfigPage /></StaffRoute>} />
        <Route path="/backoffice/escritorio" element={<StaffRoute><P.EscritorioVirtualPage /></StaffRoute>} />
        <Route path="/backoffice/dashboard" element={<StaffRoute><P.BackofficeDashboard /></StaffRoute>} />
        <Route path="/backoffice/cases" element={<StaffRoute><P.CasesListPage /></StaffRoute>} />
        <Route path="/backoffice/cases/:id" element={<StaffRoute><P.CaseDetailPage /></StaffRoute>} />
        <Route path="/backoffice/documents" element={<StaffRoute><P.DocumentsPage /></StaffRoute>} />
        <Route path="/backoffice/expedientes" element={<StaffRoute><P.ExpedientesPage /></StaffRoute>} />
        <Route path="/backoffice/clasificacion-archivistica" element={<StaffRoute><P.ClasificacionArchivisticaPage /></StaffRoute>} />
        <Route path="/backoffice/inbox" element={<StaffRoute><P.InboxPage /></StaffRoute>} />
        <Route path="/backoffice/analytics" element={<StaffRoute><P.AnalyticsPage /></StaffRoute>} />
        <Route path="/backoffice/correspondencia" element={<StaffRoute><P.CorrespondenciaPage /></StaffRoute>} />
        <Route path="/backoffice/libro" element={<StaffRoute><P.LibroGobiernoPage /></StaffRoute>} />
        <Route path="/backoffice/valijas" element={<StaffRoute><P.ValijasPage /></StaffRoute>} />
        <Route path="/backoffice/archivo" element={<StaffRoute featureFlag="advanced_archive"><P.ArchivoPage /></StaffRoute>} />
        <Route path="/backoffice/oficios" element={<StaffRoute><P.OficiosPage /></StaffRoute>} />
        <Route path="/backoffice/oficios-recibidos" element={<StaffRoute><Navigate to="/backoffice/escritorio?bandeja=recibidos" replace /></StaffRoute>} />
        <Route path="/backoffice/plantillas" element={<StaffRoute><P.PlantillasPage /></StaffRoute>} />
        <Route path="/backoffice/flujos" element={<StaffRoute><P.FlujosPage /></StaffRoute>} />
        <Route path="/backoffice/turnados" element={<StaffRoute><P.TurnadosPage /></StaffRoute>} />
        <Route path="/backoffice/firmas" element={<StaffRoute><P.FirmasPage /></StaffRoute>} />
        <Route path="/backoffice/reportes" element={<StaffRoute><P.ReportesPage /></StaffRoute>} />
        <Route path="/ayuda/glosario" element={<StaffRoute><P.GlossaryPage /></StaffRoute>} />
        <Route path="/backoffice/mifiel" element={<StaffRoute><P.MifielPage /></StaffRoute>} />
        <Route path="/backoffice/mi-efirma" element={<StaffRoute><P.MiEfirmaPage /></StaffRoute>} />
        <Route path="/backoffice/seguridad-cuenta" element={<StaffRoute><P.SeguridadCuentaPage /></StaffRoute>} />
        <Route path="/backoffice/bandeja/:bandeja" element={<StaffRoute><P.BandejaDetallePage /></StaffRoute>} />

        {/* Admin only & Specialized Operational Features */}
        <Route path="/backoffice/plataforma" element={<StaffRoute adminOnly><P.PlatformKpisPage /></StaffRoute>} />
        <Route path="/backoffice/membresia" element={<StaffRoute adminOnly><P.MembershipAdminPage /></StaffRoute>} />
        <Route path="/backoffice/integraciones" element={<StaffRoute adminOnly featureFlag="interoperability"><P.IntegrationHubPage /></StaffRoute>} />
        <Route path="/backoffice/analitica-territorial" element={<StaffRoute featureFlag="territorial_analytics"><P.GeospatialAnalyticsPage /></StaffRoute>} />
        <Route path="/backoffice/cuadrillas" element={<StaffRoute featureFlag="field_operations"><P.FieldSupervisorDashboardPage /></StaffRoute>} />
        <Route path="/backoffice/cuadrillas/movil" element={<StaffRoute featureFlag="field_mobile"><P.FieldCrewMobilePage /></StaffRoute>} />
        <Route path="/backoffice/areas" element={<StaffRoute adminOnly><P.AreasPage /></StaffRoute>} />
        <Route path="/backoffice/procedures" element={<StaffRoute adminOnly><P.BackofficeProceduresPage /></StaffRoute>} />
        <Route path="/backoffice/usuarios" element={<StaffRoute adminOnly><P.UsersManagementPage /></StaffRoute>} />

        {/* Ventanilla + admin */}
        <Route path="/backoffice/ventanilla" element={<StaffRoute ventanillaOnly><P.VentanillaPage /></StaffRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ConfirmDialogProvider>
            <AppRoutes />
            <Toaster />
          </ConfirmDialogProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
